import Head from 'next/head';
import { useRouter } from 'next/router';
import Script, { ScriptProps } from 'next/script';
import { FC, useEffect } from 'react';

import { Country } from '../../services/types';
import { axios } from '../../utils/axios';
import { assetPrefix, isProd } from '../../utils/env';

type Props = {
  cookieBanner?: boolean;
  country: Country;
  strategy?: ScriptProps['strategy'];
};

const dynamicYieldID = isProd ? '8778785' : '8778784';

type DYcookieUpdate = {
  message: string;
};

const updateDYCookie = async (dyid: string): Promise<DYcookieUpdate> => {
  const response = await axios.get(`/dy/cookie`, {
    params: {
      dyid: dyid,
    },
    withCredentials: true,
  });

  return response.data;
};

const updateCJECookie = async (cjevent: string): Promise<void> => {
  try {
    await axios.get(`/dy/cjevent`, {
      params: {
        cjevent: cjevent,
      },
      withCredentials: true,
    });
  } catch (err) {
    console.error('Error setting CJE cookie:', err);
  }
};

export const checkCookie = (cookieName: string): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(`${cookieName}=`)) {
      return true;
    }
  }
  return false;
};

export const getCookieValue = (cookieName: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(`${cookieName}=`)) {
      return cookie.substring(cookieName.length + 1);
    }
  }
  return null;
};

export const setCookie = (
  name: string,
  value: string,
  days?: number,
  sameSite: 'Lax' | 'Strict' | 'None' = 'Lax',
  secure: boolean = false,
  domain?: string,
): void => {
  if (typeof document === 'undefined') {
    return;
  }
  let expires = '';
  if (days !== undefined) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }

  let cookieString = `${name}=${value}${expires}; path=/; SameSite=${sameSite}`;

  if (secure || sameSite === 'None') {
    cookieString += '; Secure';
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  document.cookie = cookieString;
};

export const Tracking: FC<Props> = ({
  cookieBanner = true,
  strategy = 'beforeInteractive',
  country,
}) => {
  const router = useRouter();
  const scriptProps: ScriptProps = {
    strategy: strategy,
  };
  const optanonID = isProd
    ? '0b298f86-e685-42e9-b9d6-d0c8d518f0b7'
    : '0b298f86-e685-42e9-b9d6-d0c8d518f0b7-test';

  const initialType = router.asPath === '/us/' ? 'HOMEPAGE' : 'OTHER';

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get('ref');
    const cjEventParam = urlParams.get('cjevent');

    if (typeof document !== 'undefined') {
      if (refParam) {
        setCookie('ref', refParam, 14);
      }
      if (cjEventParam) {
        updateCJECookie(cjEventParam);
      }

      if (checkCookie('_dyid') && !checkCookie('_dyid_server')) {
        const dyidValue = getCookieValue('_dyid');
        if (dyidValue !== null) {
          (async (): Promise<void> => {
            try {
              const data = await updateDYCookie(dyidValue);
              if (data) {
                return;
              }
            } catch (err) {
              console.error(err);
            }
          })();
        }
      }
    }
  }, [cookieBanner]);

  return (
    <>
      <Head>
        <link rel="preconnect" href="//cdn.cookielaw.org" />
        <link rel="preconnect" href="//cdn.dynamicyield.com" />
        <link rel="preconnect" href="//st.dynamicyield.com" />
        <link rel="preconnect" href="//rcom.dynamicyield.com" />
        <link rel="dns-prefetch" href="//cdn.dynamicyield.com" />
        <link rel="dns-prefetch" href="//st.dynamicyield.com" />
        <link rel="dns-prefetch" href="//rcom.dynamicyield.com" />
      </Head>
      <Script
        id="dy-cookie"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
              window.DY = window.DY || {};
              window.DY.recommendationContext = {
                type: '${initialType}',
                data: ['${router.asPath}']
              };
              window.DY.userActiveConsent = { accepted: true };
              if (typeof DYConsent === 'function') {
                DYConsent();
              }
            `,
        }}
        strategy="afterInteractive"
      />
      <Script
        id="dy-api_dynamic"
        type="text/javascript"
        src={`//cdn.dynamicyield.com/api/${dynamicYieldID}/api_dynamic.js`}
        strategy="afterInteractive"
      />
      <Script
        id="dy-api_static"
        type="text/javascript"
        src={`//cdn.dynamicyield.com/api/${dynamicYieldID}/api_static.js`}
        strategy="afterInteractive"
      />

      <Script id="datalayer" src={assetPrefix + 'js/datalayer.js'} {...scriptProps} />

      {cookieBanner && (
        <>
          <Script
            id="onetrust"
            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
            data-domain-script={optanonID}
            {...scriptProps}
          />

          {country == 'us' ? (
            <Script
              id="dy-consent2023"
              src={`${assetPrefix}js/dy-consent2023.js`}
              {...scriptProps}
            />
          ) : (
            <Script
              id="dy-consent2023"
              src={`${assetPrefix}js/dy-consent2023.uk.js`}
              {...scriptProps}
            />
          )}

          <Script id="onetrust2023" src={`${assetPrefix}js/optanon2023.js`} {...scriptProps} />
        </>
      )}
      <noscript>
        <iframe
          src="//www.googletagmanager.com/ns.html?id=GTM-MHV4QK"
          height={0}
          width={0}
          title="GoogleAnalytics"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    </>
  );
};
